import './CreateConversationModal.scss'
import {
    Alert,
    AlertType,
    CenterManager,
    InputTextField,
    LoadingView,
    Modal,
    Role,
    SelectorField,
    TextareaField,
} from '@cuatroochenta/co-admin-react-library'
import { useTranslation } from 'react-i18next'
import { translate } from '../../../../../I18n/i18n'
import {
    ShareResidentOption,
    ShareFamiliarOption,
} from '../../../../model/domain/share-option'
import { MessagingGroup } from '../../../../model/domain/messaging'
import { useEffect, useMemo, useState } from 'react'
import { CreateConversationRequest } from '../../../../model/data/messaging'

interface CreateConversationModalProps {
    show: boolean
    messagingGroups: MessagingGroup[] | null
    residentShareOptions: ShareResidentOption[] | null
    loadingShareOptions: boolean
    loadingCreateConversation: boolean
    error: Error | null
    onConfirm: (formData: CreateConversationRequest) => void
    onClose: () => void
}

export default function CreateConversationModal({
    show,
    messagingGroups,
    residentShareOptions,
    loadingShareOptions,
    loadingCreateConversation,
    error,
    onConfirm,
    onClose,
}: CreateConversationModalProps) {
    const { t } = useTranslation()

    const [selectedMessagingGroupId, setSelectedMessagingGroupId] = useState<
        MessagingGroup['id'] | null
    >(null)
    const [selectedResidentId, setSelectedResidentId] = useState<
        ShareResidentOption['id'] | null
    >(null)
    const [selectedFamiliarId, setSelectedFamiliarId] = useState<
        ShareFamiliarOption['id'] | null
    >(null)
    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')

    const selectedResident =
        residentShareOptions?.find(
            (r) => r.id.toString() === selectedResidentId?.toString()
        ) ?? null

    const familiarOptions = useMemo(
        () => selectedResident?.suboptions ?? [],
        [selectedResident]
    )

    const sortedResidents = useMemo(() => {
        const sorted = [...(residentShareOptions ?? [])].sort((a, b) =>
            a.name.localeCompare(b.name)
        )
        return sorted.map((resident) => ({
            id: resident.id,
            description: resident.name,
        }))
    }, [residentShareOptions])

    const sortedFamiliars = useMemo(() => {
        const sorted = [...familiarOptions].sort((a, b) =>
            a.name.localeCompare(b.name)
        )
        return sorted.map((familiar) => ({
            id: familiar.id,
            description: familiar.name,
        }))
    }, [familiarOptions])

    useEffect(() => {
        if (show) {
            setSubject('')
            setBody('')
            setSelectedMessagingGroupId(null)
            setSelectedResidentId(null)
            setSelectedFamiliarId(null)
        }
    }, [show])

    useEffect(() => {
        setSelectedFamiliarId(null)
    }, [selectedResidentId])

    const handleConfirm = () => {
        onConfirm({
            subject,
            body,
            recipient: selectedFamiliarId!,
            groupId: selectedMessagingGroupId,
            roleId: Role.ADMIN,
            centerId: +CenterManager.getSelectedCenter()!
        })
    }

    const handleSubjectChange = (newSubject: string) => {
        setSubject(newSubject)
    }

    const handleMessageChange = (newMessage: string) => {
        setBody(newMessage)
    }

    const handleMessagingGroupChange = (
        newMessagingGroupId: MessagingGroup['id']
    ) => {
        setSelectedMessagingGroupId(newMessagingGroupId)
    }

    const handleResidentChange = (newResidentId: ShareResidentOption['id']) => {
        setSelectedResidentId(newResidentId)
    }

    const handleFamiliarChange = (newFamiliarId: ShareFamiliarOption['id']) => {
        setSelectedFamiliarId(newFamiliarId)
    }

    return (
        <Modal
            show={show}
            title={translate(t, 'messages.TR_CREATE_LABEL')}
            acceptText={translate(t, 'common.TR_CREATE_MODAL_SUBMIT')}
            acceptHandler={handleConfirm}
            onClose={onClose}
            children={
                <div>
                    {loadingShareOptions || loadingCreateConversation ? (
                        <LoadingView loading />
                    ) : (
                        <>
                            <Alert
                                show={!!error}
                                type={AlertType.DANGER}
                                message={translate(
                                    t,
                                    error?.message ?? 'common.TR_ERROR_GENERIC'
                                )}
                            />
                            {messagingGroups?.length ? (
                                <div className="mb-2">
                                    <label htmlFor={'messagingGroup'}>
                                        {translate(
                                            t,
                                            'messages.TR_MESSAGING_GROUP_LABEL'
                                        )}
                                    </label>
                                    <SelectorField
                                        value={
                                            selectedMessagingGroupId?.toString() ??
                                            ''
                                        }
                                        values={messagingGroups}
                                        onChange={handleMessagingGroupChange}
                                        includeAllOption={false}
                                        includePlaceholderOption
                                    />
                                </div>
                            ) : null}
                            {residentShareOptions ? (
                                <>
                                    <div className="mb-2">
                                        <label htmlFor={'resident'}>
                                            {translate(
                                                t,
                                                'messages.TR_RESIDENT_LABEL'
                                            )}
                                        </label>
                                        <SelectorField
                                            value={
                                                selectedResidentId?.toString() ??
                                                ''
                                            }
                                            values={sortedResidents}
                                            onChange={handleResidentChange}
                                            includeAllOption={false}
                                            includePlaceholderOption
                                        />
                                    </div>
                                    {selectedResidentId ? (
                                        <div className="mb-2">
                                            <label htmlFor={'familiar'}>
                                                {translate(
                                                    t,
                                                    'messages.TR_FAMILIAR_LABEL'
                                                )}
                                            </label>
                                            <SelectorField
                                                value={
                                                    selectedFamiliarId?.toString() ??
                                                    ''
                                                }
                                                values={sortedFamiliars}
                                                onChange={handleFamiliarChange}
                                                includeAllOption={false}
                                                includePlaceholderOption
                                            />
                                        </div>
                                    ) : null}
                                </>
                            ) : null}
                            <div className="mb-2">
                                <label htmlFor={'subject'}>
                                    {translate(t, 'messages.TR_SUBJECT_LABEL')}
                                </label>
                                <InputTextField
                                    name="subject"
                                    value={subject}
                                    type={'TEXT'}
                                    size={3}
                                    onChange={handleSubjectChange}
                                />
                            </div>
                            <label htmlFor={'message'}>
                                {translate(t, 'messages.TR_MESSAGE_LABEL')}
                            </label>
                            <TextareaField
                                name="message"
                                value={body}
                                onChange={handleMessageChange}
                            />
                        </>
                    )}
                </div>
            }
        />
    )
}
